// ============================================================================
// QUICK HEALTHCHECK RENDERER  v4
// Simplified 3-section layout: What's not working / What is working / Quick wins.
// Compact horizontal score block. Top risks removed.
// ============================================================================

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const arr = (a) => Array.isArray(a) ? a : [];

// Atlassian-aligned palette. soft = card bg, border = card border,
// value = number/text colour, bar = top accent bar.
const scoreTheme = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) {
    return { value: '#42526E', soft: '#F4F5F7', border: '#DFE1E6', bar: '#6B778C' };
  }
  if (n >= 7) return { value: '#0747A6', soft: '#E9F2FF', border: '#B3D4FF', bar: '#0C66E4' };
  if (n >= 4) return { value: '#974F0C', soft: '#FFF7D6', border: '#FFE380', bar: '#FF8B00' };
  return { value: '#BF2600', soft: '#FFEBE6', border: '#FFBDAD', bar: '#DE350B' };
};

// Pills are built from canonical process metrics (the shared consultant
// evidence payload), never by parsing AI prose. buildMetricPillsFromPayload
// is called at generation time in quickHealthcheck.jsx and the result is
// stored on the AI JSON as metric_pills, so saved/exported content renders
// the same pills without needing the payload again. Metrics that are not
// available are simply omitted — no "not available" pills.
const fmtNum = (n) => Number(n).toLocaleString('en-US');
const fmtHours = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 48) return `${(n / 24).toFixed(n >= 240 ? 0 : 1)} days`;
  return `${n.toFixed(n >= 10 ? 0 : 1)} hours`;
};

export const buildMetricPillsFromPayload = (payload) => {
  const ps = payload?.process_summary || {};
  const ti = payload?.time_intelligence || {};
  const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
  const pills = [];

  const variants = num(ps.variants_total);
  const items = num(ps.total_items);
  if (variants && items) pills.push(`${fmtNum(variants)} variants across ${fmtNum(items)} work items`);
  else if (variants) pills.push(`${fmtNum(variants)} variants detected`);

  const rework = num(ps.rework_rate_percent);
  if (rework !== null && rework >= 0) pills.push(`Rework rate ${rework.toFixed(rework >= 10 ? 0 : 1)}%`);

  const cycle = fmtHours(ti.avg_cycle_time_hours);
  if (cycle) pills.push(`Avg cycle time ${cycle}`);

  const flow = num(ti.flow_efficiency_percent);
  if (flow !== null && flow > 0) pills.push(`Flow efficiency ${flow.toFixed(1)}%`);

  return pills.slice(0, 4);
};

const renderDocHeader = (modelTitle) => {
  const ts = new Date().toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  });
  return `
    <header class="doc-head">
      <div class="doc-head-left">
        <div class="doc-eyebrow">Quick Healthcheck</div>
        <h1 class="doc-title">${esc(modelTitle || 'Process Model')}</h1>
      </div>
      <div class="doc-stamp">Generated ${esc(ts)}</div>
    </header>`;
};

const renderHero = (data) => {
  const health = data?.overall_health || {};
  const theme = scoreTheme(health.score);
  const pills = arr(data?.metric_pills);
  const confidenceText = health?.confidence
    ? `Confidence ${String(health.confidence).toLowerCase()}`
    : '';

  return `
    <section class="hero" style="--accent:${theme.bar};">
      <div class="hero-main">
        <p class="hero-summary">${esc(health?.rationale || 'High-level health summary is not available.')}</p>
        ${pills.length ? `<div class="hero-support">${pills.map(p => `<span class="pill">${esc(p)}</span>`).join('')}</div>` : ''}
      </div>
      <aside class="score" style="background:${theme.soft};border-color:${theme.border};">
        <div class="score-num" style="color:${theme.value};">${esc(health?.score ?? '—')}<span class="score-denom">/10</span></div>
        <div class="score-meta">
          <div class="score-label">Health Score</div>
          ${health?.rating ? `<div class="score-state" style="color:${theme.value};">${esc(health.rating)}</div>` : ''}
          ${confidenceText ? `<div class="score-confidence">${esc(confidenceText)}</div>` : ''}
        </div>
      </aside>
    </section>`;
};

// title is pre-built HTML (escaped text + any inline markup). Callers escape their own text.
const renderItem = (cls, titleHtml, body, extra = '') => `
  <div class="item row ${cls}">
    <div class="marker"></div>
    <div class="item-main">
      <h3 class="item-title">${titleHtml}</h3>
      ${body}
      ${extra}
    </div>
  </div>`;

const renderNotWorkingPanel = (data) => {
  const items = arr(data?.whats_not_working);
  const content = items.length
    ? items.map(n => renderItem(
        'issue',
        esc(n.title || ''),
        `<p class="item-body">${esc(n.evidence || '')}</p>`,
        n.interpretation ? `<p class="item-body muted">${esc(n.interpretation)}</p>` : ''
      )).join('')
    : `<p class="empty">No major attention areas surfaced in the evidence.</p>`;

  return `
    <section class="panel">
      <h2 class="panel-title">What's not working</h2>
      <div class="item-list">${content}</div>
    </section>`;
};

const renderWorkingPanel = (data) => {
  const items = arr(data?.whats_working);
  const content = items.length
    ? items.map(w => renderItem(
        'good',
        esc(w.title || ''),
        `<p class="item-body">${esc(w.evidence || '')}</p>`
      )).join('')
    : `<p class="empty">No strong positives surfaced in the evidence.</p>`;

  return `
    <section class="panel">
      <h2 class="panel-title">What is working</h2>
      <div class="item-list">${content}</div>
    </section>`;
};

const renderWinsPanel = (data) => {
  const items = arr(data?.quick_wins);
  const content = items.length
    ? items.map(w => renderItem(
        'win',
        esc(w.title || ''),
        `<p class="item-body">${esc(w.action || '')}</p>`,
        w.expected_benefit ? `<div class="benefit">Expected benefit: ${esc(w.expected_benefit)}</div>` : ''
      )).join('')
    : `<p class="empty">No obvious quick wins surfaced.</p>`;

  return `
    <section class="panel panel-wins">
      <h2 class="panel-title">Quick wins</h2>
      <div class="item-grid">${content}</div>
    </section>`;
};

const renderFooter = (data) => `
  <div class="footer-note">
    ${data?.consultant_note ? `<div class="label">Suggested discussion opener</div><p>${esc(data.consultant_note)}</p>` : ''}
    <div class="deeper-note">For deeper analysis, open <strong>Process Health</strong>, <strong>Execution Strategy</strong>, or the <strong>Assessment Report</strong>.</div>
  </div>`;

const renderBody = (data, modelTitle, includeHeader = true) => `
  ${includeHeader ? renderDocHeader(modelTitle) : ''}
  ${renderHero(data)}
  <div class="layout-pair">
    ${renderNotWorkingPanel(data)}
    ${renderWorkingPanel(data)}
  </div>
  ${renderWinsPanel(data)}
  ${renderFooter(data)}`;

const CSS = `
  :root{
    --surface:#FFFFFF;
    --surface-alt:#FAFBFC;
    --border:#DFE1E6;
    --border-soft:#EBECF0;
    --text:#172B4D;
    --text-strong:#091E42;
    --muted:#42526E;
    --subtle:#6B778C;
    --blue:#0C66E4;
    --blue-strong:#0747A6;
    --blue-soft:#E9F2FF;
    --green:#1F845A;
    --green-strong:#216E4E;
    --amber:#FF8B00;
    --amber-strong:#974F0C;
    --red:#DE350B;
    --red-strong:#BF2600;
    --shadow-sm:0 1px 2px rgba(9,30,66,.08);
  }
  *{box-sizing:border-box}
  html,body{margin:0;padding:0}
  body{
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,'Helvetica Neue',Arial,sans-serif;
    background:#F7F8FA;
    color:var(--text);
    line-height:1.5;
    padding:20px 24px 24px;
    font-size:14px;
  }
  .wrap{max-width:1180px;margin:0 auto}

  /* ---- Document header ---- */
  .doc-head{
    display:flex;justify-content:space-between;align-items:flex-end;gap:20px;
    padding:0 4px 14px;margin-bottom:14px;
    border-bottom:1px solid var(--border);
  }
  .doc-eyebrow{
    font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;
    color:var(--blue);margin-bottom:4px;
  }
  .doc-title{
    margin:0;font-size:22px;line-height:1.2;letter-spacing:-.02em;
    color:var(--text-strong);font-weight:700;
  }
  .doc-stamp{font-size:12px;color:var(--subtle);white-space:nowrap}

  /* ---- Hero (compact horizontal score) ---- */
  .hero{
    background:var(--surface);
    border:1px solid var(--border);
    border-radius:10px;
    box-shadow:var(--shadow-sm);
    padding:16px 18px;
    display:grid;grid-template-columns:minmax(0,1fr) auto;gap:18px;
    align-items:center;
    margin-bottom:14px;
    position:relative;overflow:hidden;
  }
  .hero::before{
    content:'';position:absolute;top:0;left:0;right:0;height:3px;
    background:var(--accent, var(--blue));
  }
  .hero-main{min-width:0}
  .hero-summary{
    font-size:15px;line-height:1.45;color:var(--text-strong);
    font-weight:600;margin:0;max-width:780px;
  }
  .hero-support{display:flex;gap:6px;flex-wrap:wrap;margin-top:10px}
  .pill{
    display:inline-flex;align-items:center;padding:3px 9px;border-radius:999px;
    font-size:11px;font-weight:600;
    background:var(--surface-alt);border:1px solid var(--border-soft);color:var(--muted);
  }
  .score{
    border:1px solid var(--border);border-radius:10px;
    padding:10px 14px;
    display:flex;align-items:center;gap:12px;
    min-width:180px;
  }
  .score-num{
    font-size:36px;line-height:1;font-weight:800;letter-spacing:-.04em;
    display:flex;align-items:baseline;
  }
  .score-denom{font-size:14px;font-weight:700;opacity:.6;margin-left:2px}
  .score-meta{display:flex;flex-direction:column;gap:2px;min-width:0}
  .score-label{
    font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;
    color:var(--subtle);
  }
  .score-state{font-size:12px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;line-height:1.2}
  .score-confidence{font-size:11px;color:var(--subtle);line-height:1.3}

  /* ---- Paired columns (not working + working) ---- */
  .layout-pair{
    display:grid;grid-template-columns:1fr 1fr;gap:14px;
    margin-bottom:14px;
  }
  .layout-pair .panel{margin:0}

  /* Quick wins uses a 2-col internal grid so the full width is used. */
  .panel-wins .item-grid{
    display:grid;grid-template-columns:1fr 1fr;gap:10px 20px;
  }
  .panel-wins .item{border-bottom:0;padding-bottom:0}
  @media (max-width: 720px){
    .panel-wins .item-grid{grid-template-columns:1fr}
  }

  /* ---- Panels ---- */
  .panel{
    background:var(--surface);border:1px solid var(--border);border-radius:10px;
    box-shadow:var(--shadow-sm);padding:16px 18px;
  }
  .panel-title{margin:0 0 4px;font-size:16px;line-height:1.25;letter-spacing:-.01em;color:var(--text-strong);font-weight:700}
  .section-intro{margin:0 0 12px;font-size:13px;color:var(--subtle)}
  .item-list{display:flex;flex-direction:column;gap:10px}
  .item{padding:0 0 10px;border-bottom:1px solid var(--border-soft)}
  .item:last-child{padding-bottom:0;border-bottom:0}
  .row{display:grid;grid-template-columns:8px minmax(0,1fr);gap:10px;align-items:start}
  .marker{width:8px;height:8px;border-radius:999px;flex:0 0 8px;margin-top:6px}
  .item-main{min-width:0}
  .item-title{margin:0;font-size:14px;line-height:1.35;font-weight:700;letter-spacing:-.005em;color:var(--text-strong)}
  .item-body{margin:3px 0 0;font-size:13px;line-height:1.5;color:var(--muted)}
  .item-body.muted{color:var(--subtle)}
  .benefit{margin-top:4px;font-size:12px;color:var(--green-strong);font-weight:600}

  /* ---- Category colours ---- */
  .good .marker{background:var(--green)}
  .good .item-title{color:var(--green-strong)}
  .issue .marker{background:var(--red)}
  .issue .item-title{color:var(--red-strong)}
  .win .marker{background:var(--blue)}
  .win .item-title{color:var(--blue-strong)}

  /* ---- Footer ---- */
  .footer-note{
    margin-top:14px;padding:14px 16px;border-radius:10px;
    background:var(--blue-soft);border:1px solid #CCE0FF;color:var(--text);
  }
  .footer-note .label{
    font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;
    color:var(--blue-strong);margin-bottom:4px;
  }
  .footer-note p{margin:0;font-size:13px;line-height:1.55}
  .deeper-note{
    margin-top:10px;padding-top:10px;
    border-top:1px solid rgba(12,102,228,.18);
    font-size:12px;color:var(--muted);
  }
  .empty{margin:0;font-size:13px;color:#97A0AF;font-style:italic}

  /* ---- Responsive ---- */
  @media (max-width: 860px){
    .hero{grid-template-columns:1fr}
    .score{min-width:0}
    .layout-pair{grid-template-columns:1fr}
  }
  @media (max-width: 620px){
    body{padding:14px}
    .doc-head{display:block}
    .doc-stamp{margin-top:6px}
    .doc-title{font-size:20px}
    .hero{padding:14px}
    .panel{padding:14px}
  }
  @media print{
    body{background:#fff;padding:0}
    .hero,.panel,.footer-note{break-inside:avoid;box-shadow:none}
  }
`;

const wrap = (body, modelTitle) => `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<meta name="renderer-version" content="v2026-04-19-quick-healthcheck-v4"/>
<title>Quick Healthcheck${modelTitle ? ' — ' + modelTitle.replace(/</g, '&lt;') : ''}</title>
<style>${CSS}</style>
</head>
<body>
  <div class="wrap">${body}</div>
</body>
</html>`;

export const renderQuickHealthcheckHTML = (data, modelTitle = '') => {
  if (!data || typeof data !== 'object') return wrap('<p style="padding:24px;color:#6B778C;">No data.</p>', modelTitle);
  return wrap(renderBody(data, modelTitle, true), modelTitle);
};

export const buildQuickHealthcheckExportHTML = (data, modelTitle = '') => {
  if (!data || typeof data !== 'object') return wrap('<p style="padding:24px;color:#6B778C;">No data.</p>', modelTitle);
  return wrap(renderBody(data, modelTitle, true), modelTitle);
};

export default renderQuickHealthcheckHTML;